import { useEffect, useId, useRef, useState } from "react";

import { useOverlayDismiss } from "../../lib/useOverlayDismiss";
import { ApiError, apiClient } from "../../api/client";
import type { Department, MeResponse, Position, PositionInput } from "../../api/types";
import { useIdempotencyKey } from "../../lib/idempotencyKey";
import { parseVideo } from "../../lib/videoEmbed";
import { Link } from "../../router/Router";
import { RichTextEditor } from "./RichTextEditor";
import { VideoEmbed } from "./VideoEmbed";

export const EMPLOYMENT_TYPES = [
  { value: "full_time", label: "Full-time" },
  { value: "part_time", label: "Part-time" },
  { value: "contract", label: "Contract" },
  { value: "internship", label: "Internship" }
] as const;

export function employmentTypeLabel(value: string): string {
  const found = EMPLOYMENT_TYPES.find((t) => t.value === value);
  return found !== undefined ? found.label : value;
}

const MAX_VIDEOS = 10;

interface VideoRow {
  // Stable identity so reorder/remove key by row, not array index — otherwise
  // React reuses DOM nodes and the preview iframe reloads on every move.
  id: string;
  provider: "loom" | "youtube";
  url: string;
  label: string;
}

// PositionFormModal creates or edits a position. Videos are repeatable rows
// with a live preview (the same parser the server validates with); the first
// video becomes the card's primary. Contact email defaults to the creator's.
export function PositionFormModal({
  me,
  position,
  onClose,
  onSaved
}: {
  me: MeResponse;
  position: Position | null; // null = create
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = position !== null;
  const [departments, setDepartments] = useState<Department[]>([]);
  const [departmentsStatus, setDepartmentsStatus] = useState<"loading" | "ready" | "error">("loading");
  const [deptReloadKey, setDeptReloadKey] = useState(0);
  const rowIdSeq = useRef(0);
  const [title, setTitle] = useState(position?.title ?? "");
  const [departmentId, setDepartmentId] = useState(position?.department_id ?? "");
  // description is the editor's HTML; emptiness comes from the editor
  // (Tiptap's empty document is "<p></p>", not "").
  const [description, setDescription] = useState(position?.description ?? "");
  const [descriptionEmpty, setDescriptionEmpty] = useState(position === null);
  const [slots, setSlots] = useState(position?.slots ?? 1);
  const [employmentType, setEmploymentType] = useState(position?.employment_type ?? "");
  const [location, setLocation] = useState(position?.location ?? "");
  const [compensationRange, setCompensationRange] = useState(position?.compensation_range ?? "");
  const [contactEmail, setContactEmail] = useState(position?.contact_email ?? me.user.email);
  const [videos, setVideos] = useState<VideoRow[]>(
    position?.videos.map((v) => ({ id: v.id, provider: v.provider, url: v.url, label: v.label ?? "" })) ?? []
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { keyFor } = useIdempotencyKey();
  const titleId = useId();
  const overlayDismiss = useOverlayDismiss(onClose, submitting);

  useEffect(() => {
    let cancelled = false;
    setDepartmentsStatus("loading");
    apiClient
      .listDepartments()
      .then((response) => {
        if (cancelled) {
          return;
        }
        setDepartments(response.departments);
        setDepartmentsStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setDepartmentsStatus("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [deptReloadKey]);

  function addVideo() {
    if (videos.length >= MAX_VIDEOS) {
      return;
    }
    rowIdSeq.current += 1;
    setVideos((previous) => [...previous, { id: `new-${rowIdSeq.current}`, provider: "loom", url: "", label: "" }]);
  }

  function updateVideo(index: number, patch: Partial<VideoRow>) {
    setVideos((previous) => previous.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function removeVideo(index: number) {
    setVideos((previous) => previous.filter((_, i) => i !== index));
  }

  function moveVideo(index: number, delta: -1 | 1) {
    setVideos((previous) => {
      const target = index + delta;
      if (target < 0 || target >= previous.length) {
        return previous;
      }
      const next = [...previous];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  const videosValid = videos.every((v) => parseVideo(v.provider, v.url) !== null);
  const canSubmit =
    !submitting &&
    title.trim() !== "" &&
    departmentId !== "" &&
    !descriptionEmpty &&
    slots >= 1 &&
    contactEmail.trim() !== "" &&
    videosValid;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    // Optional single-line fields are always sent (empty string clears them to
    // NULL server-side). Omitting a key means "unchanged" on the PATCH, so a
    // conditional spread would make cleared fields impossible to clear on edit.
    const input: PositionInput = {
      title: title.trim(),
      department_id: departmentId,
      description,
      slots,
      contact_email: contactEmail.trim(),
      employment_type: employmentType,
      location: location.trim(),
      compensation_range: compensationRange.trim(),
      videos: videos.map((v) => ({
        provider: v.provider,
        url: v.url.trim(),
        ...(v.label.trim() !== "" ? { label: v.label.trim() } : {})
      }))
    };
    setSubmitting(true);
    setError(null);
    try {
      if (position !== null) {
        await apiClient.updatePosition(position.id, input, keyFor(`edit:${position.id}:${JSON.stringify(input)}`));
      } else {
        await apiClient.createPosition(input, keyFor(`create:${JSON.stringify(input)}`));
      }
      onSaved();
    } catch (caught) {
      setError(messageFor(caught));
      setSubmitting(false);
    }
  }

  const noDepartments = departmentsStatus === "ready" && departments.length === 0;

  return (
    <div className="modal-overlay" role="presentation" {...overlayDismiss}>
      <div
        className="modal position-form-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="modal__header">
          <h2 id={titleId}>{editing ? "Edit position" : "New position"}</h2>
          <button type="button" className="modal__close" aria-label="Close" onClick={onClose} disabled={submitting}>
            ×
          </button>
        </header>
        <div className="modal__body">
          {departmentsStatus === "error" ? (
            <div className="empty-state">
              <p className="form-row__error">Couldn't load departments.</p>
              <p>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => setDeptReloadKey((key) => key + 1)}
                >
                  Retry
                </button>
              </p>
            </div>
          ) : noDepartments ? (
            <div className="empty-state">
              <p>Positions need a department, and none exist yet.</p>
              <p>
                <Link to="/hiring/departments" className="btn btn--primary btn--sm">
                  Manage departments
                </Link>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <label>
                  Title
                  <input
                    type="text"
                    value={title}
                    maxLength={200}
                    onChange={(event) => setTitle(event.target.value)}
                    placeholder="Senior Backend Engineer"
                    required
                  />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Department
                  <select value={departmentId} onChange={(event) => setDepartmentId(event.target.value)} required>
                    <option value="">Select a department…</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="muted">
                  <Link to="/hiring/departments">Manage departments</Link>
                </p>
              </div>
              <div className="form-row">
                <label>
                  Description
                  <RichTextEditor
                    initialHTML={description}
                    onChange={(html, isEmpty) => {
                      setDescription(html);
                      setDescriptionEmpty(isEmpty);
                    }}
                  />
                </label>
              </div>
              <div className="form-row form-row--inline">
                <label>
                  Openings
                  <input
                    type="number"
                    min={1}
                    value={slots}
                    onChange={(event) => setSlots(Math.max(1, Number(event.target.value) || 1))}
                    required
                  />
                </label>
                <label>
                  Employment type
                  <select value={employmentType} onChange={(event) => setEmploymentType(event.target.value)}>
                    <option value="">Not specified</option>
                    {EMPLOYMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="form-row form-row--inline">
                <label>
                  Location
                  <input
                    type="text"
                    value={location}
                    maxLength={120}
                    onChange={(event) => setLocation(event.target.value)}
                    placeholder="Remote / NYC"
                  />
                </label>
                <label>
                  Compensation range
                  <input
                    type="text"
                    value={compensationRange}
                    maxLength={120}
                    onChange={(event) => setCompensationRange(event.target.value)}
                    placeholder="$120k–$150k"
                  />
                </label>
              </div>
              <div className="form-row">
                <label>
                  Contact email
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(event) => setContactEmail(event.target.value)}
                    required
                  />
                </label>
                <p className="muted">Responses go here. Never shown to viewers.</p>
              </div>

              <fieldset className="position-form__videos">
                <legend>Videos ({videos.length}/{MAX_VIDEOS})</legend>
                <p className="muted">
                  Loom or unlisted YouTube links. The first video is the card's preview.
                </p>
                {videos.map((video, index) => {
                  const parsed = video.url.trim() !== "" ? parseVideo(video.provider, video.url) : null;
                  const showInvalid = video.url.trim() !== "" && parsed === null;
                  return (
                    <div key={video.id} className="position-form__video-row">
                      <div className="form-row form-row--inline">
                        <label>
                          Provider
                          <select
                            value={video.provider}
                            onChange={(event) => updateVideo(index, { provider: event.target.value as "loom" | "youtube" })}
                          >
                            <option value="loom">Loom</option>
                            <option value="youtube">YouTube (unlisted)</option>
                          </select>
                        </label>
                        <label>
                          URL
                          <input
                            type="url"
                            value={video.url}
                            maxLength={2048}
                            onChange={(event) => updateVideo(index, { url: event.target.value })}
                            placeholder={
                              video.provider === "loom"
                                ? "https://www.loom.com/share/…"
                                : "https://www.youtube.com/watch?v=…"
                            }
                          />
                        </label>
                        <label>
                          Label
                          <input
                            type="text"
                            value={video.label}
                            maxLength={120}
                            onChange={(event) => updateVideo(index, { label: event.target.value })}
                            placeholder="Role pitch"
                          />
                        </label>
                      </div>
                      <div className="position-form__video-actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => moveVideo(index, -1)}
                          disabled={index === 0}
                          aria-label={`Move video ${index + 1} up`}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => moveVideo(index, 1)}
                          disabled={index === videos.length - 1}
                          aria-label={`Move video ${index + 1} down`}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => removeVideo(index)}
                          aria-label={`Remove video ${index + 1}`}
                        >
                          Remove
                        </button>
                      </div>
                      {showInvalid && (
                        <p className="form-row__error">
                          That URL doesn't match a {video.provider === "loom" ? "Loom share" : "YouTube video"} link.
                        </p>
                      )}
                      {parsed !== null && <VideoEmbed embedUrl={parsed.embedUrl} title="Video preview" />}
                    </div>
                  );
                })}
                {videos.length < MAX_VIDEOS && (
                  <button type="button" className="btn btn--ghost btn--sm" onClick={addVideo}>
                    Add video
                  </button>
                )}
              </fieldset>

              {error !== null && <p className="form-row__error">{error}</p>}
              <div className="modal__footer">
                <button type="button" className="btn btn--ghost" onClick={onClose} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
                  {submitting ? "Saving…" : editing ? "Save changes" : "Create position"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Couldn't save position.";
}
